import { WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE } from "./capability.js";
/** `auth_session.scope_kind`'s three values, in the order the state machine reaches them. */
export const AUTH_CONTEXT_KIND = ["selection_required", "account_only", "workspace_selected"];
/** The HTTP header a cookie request carries its scope generation in, and every scoped answer echoes. */
export const SCOPE_GENERATION_HEADER = "x-wetalk-scope-generation";
/**
 * Builds the context of an auth_session from its row's scope and the person's current access.
 *
 * `scope.scopeKind` is cross-checked against the row's ids: the database generates one from the
 * other, so a disagreement is a storage fault and raises rather than being resolved either way.
 */
export function authContextOf(authSession, scope, choice) {
    const expected = authSession.accountId === null ? "selection_required" : authSession.workspaceId === null ? "account_only" : "workspace_selected";
    if (expected !== scope.scopeKind) {
        throw new Error(`auth_session ${scope.authSessionId}: scope_kind ${scope.scopeKind} disagrees with its ids (${expected}); ` +
            "the column is generated from them, so this is a storage fault");
    }
    if (authSession.accountId === null) {
        return { kind: "selection_required", scopeGeneration: scope.scopeGeneration, assurance: scope.assurance };
    }
    const held = choice.find((one) => one.accountId === authSession.accountId);
    if (held === undefined)
        return { refused: "membership_revoked" };
    const account = {
        accountId: held.accountId,
        accountName: held.accountName,
        accountState: held.accountState,
    };
    if (authSession.workspaceId === null) {
        if (held.accountRole === null)
            return { refused: "membership_revoked" };
        return {
            kind: "account_only",
            scopeGeneration: scope.scopeGeneration,
            assurance: scope.assurance,
            account,
            accountRole: held.accountRole,
        };
    }
    const workspace = held.workspace.find((one) => one.workspaceId === authSession.workspaceId);
    if (workspace === undefined)
        return { refused: "membership_revoked" };
    return {
        kind: "workspace_selected",
        scopeGeneration: scope.scopeGeneration,
        assurance: scope.assurance,
        account,
        accountRole: held.accountRole,
        workspace: {
            workspaceId: workspace.workspaceId,
            workspaceName: workspace.workspaceName,
            workspaceState: workspace.workspaceState,
            memberRole: workspace.memberRole,
        },
    };
}
// ---------------------------------------------------------------------------------------
// The capability listing — the checked-in matrix (design §27.5.6), restated
// ---------------------------------------------------------------------------------------
/**
 * The checked-in matrix (design §27.5.6). AMENDED 2026-10-05 (plan-014 A3): A2 restated it here so
 * the listing needed no file read at run time; the canonical evaluator (`./capability.ts`) now owns
 * the restatement, `capability.test.ts` holds it EQUAL to the fixture cell by cell, and this module
 * re-exports it so the listing and every decision read one object.
 */
export { CAPABILITY_MATRIX } from "./capability.js";
function heldBy(map, role) {
    if (role === null)
        return [];
    return Object.entries(map)
        .filter(([, roles]) => roles.includes(role))
        .map(([capability]) => capability);
}
/**
 * The capabilities a context's roles hold, in the matrix's order: the account role's account
 * capabilities and — only with a workspace selected — the member role's workspace capabilities.
 *
 * AMENDED 2026-10-09 (ADR 0041): with a workspace selected, also the workspace capabilities the account
 * role holds on every workspace of the account (`WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE`), still in the
 * matrix's order — the listing answers what the evaluator would.
 */
export function contextCapabilityOf(context, matrix) {
    if (context.kind === "selection_required")
        return { account: [], workspace: [] };
    const account = heldBy(matrix.account, context.accountRole);
    if (context.kind !== "workspace_selected")
        return { account, workspace: [] };
    const byMember = new Set(heldBy(matrix.workspace, context.workspace.memberRole));
    const byAccount = new Set(heldBy(WORKSPACE_CAPABILITY_BY_ACCOUNT_ROLE, context.accountRole));
    const workspace = Object.keys(matrix.workspace).filter((one) => byMember.has(one) || byAccount.has(one));
    return { account, workspace };
}
// ---------------------------------------------------------------------------------------
// Landing (design §27.6.3, FR12-AUTH-10)
// ---------------------------------------------------------------------------------------
/** Where the console lands a context. Copy-free: the console maps each to its route. */
export const LANDING = ["workspace", "finance", "organization", "selection"];
/**
 * The landing of a context (design §27.6.3): a workspace context lands in its workspace; an
 * account-only context whose role holds the money but not the organization's administration lands
 * on Finance (the prototype's `land.bella = ['org','funding']`); any other account-only context
 * lands on the Organization; an unscoped one on the picker.
 */
export function landingOf(context, capability) {
    if (context.kind === "selection_required")
        return "selection";
    if (context.kind === "workspace_selected")
        return "workspace";
    const financeOnly = capability.account.includes("account.finance.read") && !capability.account.includes("account.profile.write");
    return financeOnly ? "finance" : "organization";
}
//# sourceMappingURL=auth-context.js.map